<template>
  <div class="home-page">
    <Hero/>
    <Who/>
    <Solutions/>
    <Features/>
    <Projects/>
    <Clients/>
    <Cta/>
  </div>
</template>

<script>
import { smoothScrollTo } from '@/utils/smoothScroll'

export default {
  name: 'HomePage',
  data() {
    return {
      isMenuActive: false,
      sectionObserver: null,
      gsapContext: null,
      scrollRestoreKey: '',
      isRestoringScroll: false
    };
  },
  async mounted() {
    this.initScrollRestoration();
    this.initChatIcon();

    if (!window.location.hash) {
      await this.restoreScrollPosition();
    }

    try {
      await this.loadGsapScripts();
      this.initGsapAnimations();
    } catch (error) {
      console.error(error);
    }
  },
  beforeUnmount() {
    // Очистка событий при размонтировании компонента
    if (this.gsapContext) {
      this.gsapContext.revert();
      this.gsapContext = null;
    }
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }
    window.removeEventListener('scroll', this.saveScrollPosition);
    window.removeEventListener('beforeunload', this.saveScrollPosition);
  },
  methods: {
    initScrollRestoration() {
      if (typeof window === 'undefined') return;

      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }

      this.scrollRestoreKey = `scroll:${window.location.pathname}`;
      window.addEventListener('scroll', this.saveScrollPosition, { passive: true });
      window.addEventListener('beforeunload', this.saveScrollPosition);
    },

    saveScrollPosition() {
      if (!this.scrollRestoreKey || this.isRestoringScroll) return;
      sessionStorage.setItem(this.scrollRestoreKey, String(window.scrollY || window.pageYOffset || 0));
    },

    restoreScrollPosition() {
      if (!this.scrollRestoreKey) return Promise.resolve();

      const raw = sessionStorage.getItem(this.scrollRestoreKey);
      const targetY = Number(raw);
      if (!Number.isFinite(targetY) || targetY <= 0) return Promise.resolve();

      this.isRestoringScroll = true;
      let attempts = 0;
      const maxAttempts = 40;

      return new Promise((resolve) => {
        const tryRestore = () => {
          attempts += 1;
          const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;

          if (maxScrollY >= targetY || attempts >= maxAttempts) {
            window.scrollTo(0, Math.min(targetY, Math.max(0, maxScrollY)));
            this.isRestoringScroll = false;
            resolve();
            return;
          }

          setTimeout(tryRestore, 100);
        };

        requestAnimationFrame(tryRestore);
      });
    },

    async loadGsapScripts() {
      const sources = [
        'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js',
        'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js'
      ];

      for (const src of sources) {
        await this.loadScriptOnce(src);
      }
    },

    loadScriptOnce(src) {
      return new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          if (existingScript.dataset.loaded === 'true') {
            resolve();
            return;
          }

          existingScript.addEventListener('load', () => resolve(), { once: true });
          existingScript.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => {
          script.dataset.loaded = 'true';
          resolve();
        };
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(script);
      });
    },

    initGsapAnimations() {
      if (!window.gsap || !window.ScrollTrigger) return;
      if (this.gsapContext) return;

      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);
      this.gsapContext = gsap.context(() => {

      // Who section title animation
      gsap.from(".section-who .section__title > *", {
        y: "100%",
        scale: 0.85,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        stagger: 0.1,
        scrollTrigger: {
          trigger: ".section-who .section__title",
          start: "top 80%",
          toggleActions: "play none none none"
        }
      });

      // About cards animation
      const cardsWho = gsap.utils.toArray(".card-horizontal, .card-vertical");
      const tlWho = gsap.timeline({
        scrollTrigger: {
          trigger: ".who__container",
          start: "top 80%",
          toggleActions: "play none none none",
          once: true
        }
      });

      tlWho.from(gsap.utils.shuffle(cardsWho), {
        opacity: 0,
        y: 50,
        scale: 0.9,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.2
      });

      // Solution section title animation
      gsap.from(".section-solution .section__title", {
        y: "100%",
        scale: 0.85,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        stagger: 0.1,
        scrollTrigger: {
          trigger: ".section-solution",
          start: "top 80%",
          toggleActions: "play none none none"
        }
      });

      // Features section animations
      gsap.from(".section-features .section__title", {
        y: "100%",
        scale: 0.85,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        stagger: 0.1,
        scrollTrigger: {
          trigger: ".section-features",
          start: "top 80%",
          toggleActions: "play none none none"
        }
      });

      gsap.from(".features__left-block", {
        opacity: 0,
        y: 50,
        duration: 1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".features__wrapper",
          start: "top 80%",
          toggleActions: "play none none none"
        }
      });

      // Features accordion items animation
      gsap.utils.toArray(".features__right-block [data-accordion-item]").forEach(item => {
        gsap.from(item, {
          opacity: 0,
          y: 30,
          duration: 0.3,
          ease: "power2.out",
          scrollTrigger: {
            trigger: item,
            start: "top 80%",
            toggleActions: "play none none none",
            once: true
          }
        });
      });

      // Cases section animations
      gsap.from(".section-cases h4", {
        y: "100%",
        scale: 0.85,
        opacity: 0,
        duration: 1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".section-cases",
          start: "top 80%",
          toggleActions: "play none none none",
          once: true
        }
      });

      const cardsCases = gsap.utils.toArray(".cases__container .cases-item");
      const tlCases = gsap.timeline({
        scrollTrigger: {
          trigger: ".cases__container",
          start: "top 80%",
          toggleActions: "play none none none",
          once: true
        }
      });

      tlCases.from(gsap.utils.shuffle(cardsCases), {
        opacity: 0,
        scale: 0.8,
        duration: 0.6,
        ease: "power2.out",
        stagger: 0.15
      });

      // CTA section animations (free split by words)
      const ctaTitle = document.querySelector(".cta-title");
      if (ctaTitle && !ctaTitle.querySelector(".word")) {
        const words = ctaTitle.textContent?.trim().split(/\s+/) || [];
        ctaTitle.textContent = "";
        words.forEach((word, idx) => {
          const span = document.createElement("span");
          span.className = "word";
          span.textContent = word;
          ctaTitle.appendChild(span);
          if (idx !== words.length - 1) {
            ctaTitle.appendChild(document.createTextNode(" "));
          }
        });
      }

      const tlCta = gsap.timeline({
        scrollTrigger: {
          trigger: ".cta-title",
          start: "top 80%",
          once: true
        }
      });

      tlCta.from(".word", {
        x: -250,
        opacity: 0,
        duration: 0.6,
        ease: "power2.out",
        stagger: 0
      })
        .from(".cta-title .span-accent", {
          x: 250,
          opacity: 0,
          duration: 0.6,
          ease: "power2.out"
        }, ">0.2")
        .to(".section-cta [data-openForm]", {
          opacity: 1,
          scale: 1,
          duration: 0.3,
          ease: "power2.out"
        }, ">-0.15")
        .from(".cta-deco-01", {
          opacity: 0,
          scale: 0.8,
          duration: 0.3,
          ease: "power2.out"
        }, ">-0.15")
        .from(".cta-deco-02", {
          opacity: 0,
          scale: 0.8,
          duration: 0.3,
          ease: "power2.out"
        });

      // Footer animations
      const tlFooter = gsap.timeline({
        scrollTrigger: {
          trigger: ".footer",
          start: "top 80%",
          once: true
        }
      });

      tlFooter.from(".footer__left > *, .footer__right > *, .footer__bottom > *", {
        opacity: 0,
        y: "100%",
        scale: 0.8,
        duration: 0.3,
        ease: "power2.out",
        stagger: 0.15
      });
      ScrollTrigger.refresh();
      });
    },
    // Инициализация чат-иконки
    initChatIcon() {
      const chatIcon = document.querySelector('[data-chat-icon]');
      if (chatIcon) {
        chatIcon.addEventListener('click', () => {
          console.log('Chat icon clicked');
          // Ваша логика для чат-иконки
        });
      }
    },

    initSectionReveal() {},

    // Скролл к hash в URL
    scrollToHash() {
      const hash = window.location.hash.substring(1);
      const element = document.getElementById(hash);
      if (!element) return Promise.resolve();

      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          smoothScrollTo(element);
          resolve();
        });
      });
    }
    ,
  }
};
</script>

<style lang="scss" scoped>
.home-page {
  display: contents;
  padding-left: 0;
  padding-right: 0;
  width: 100%;
}

.home-page > * {
  padding-left: 7.5em;
  padding-right: 7.5em;
}

@media screen and (max-width: 479px) {
  .home-page > * {
    padding-left: 1.25em;
    padding-right: 1.25em;
  }
}
</style>

<style scoped>
</style>
